// Parsers for the "text stream" and Prometheus-exposition sample formats the
// auto-mapping screen accepts. Pure text in, plain values out.
import type { JsonValue } from './mappingEngine';

// logfmt: key=value key2="quoted value". Needs at least one pair and no
// stray words, otherwise the line is treated as free text.
export function parseLogfmt(line: string): Record<string, JsonValue> | null {
  const out: Record<string, JsonValue> = {};
  const re = /([A-Za-z_][\w.-]*)=("(?:[^"\\]|\\.)*"|\S*)/g;
  let last = 0;
  let n = 0;
  for (let m = re.exec(line); m; m = re.exec(line)) {
    if (line.slice(last, m.index).trim() !== '') return null;
    last = m.index + m[0].length;
    out[m[1]] = m[2].startsWith('"') ? (JSON.parse(m[2]) as string) : m[2];
    n++;
  }
  if (n === 0 || line.slice(last).trim() !== '') return null;
  return out;
}

// One Record per line: a JSON object, else logfmt, else { line: "<text>" }.
export function parseTextStream(text: string, max = 200): Record<string, JsonValue>[] {
  const out: Record<string, JsonValue>[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    if (out.length >= max) break;
    if (line.startsWith('{')) {
      try {
        const v = JSON.parse(line);
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          out.push(v as Record<string, JsonValue>);
          continue;
        }
      } catch {
        /* fall through */
      }
    }
    out.push(parseLogfmt(line) ?? { line });
  }
  return out;
}

export interface PromSample {
  name: string;
  labels: Record<string, string>;
  value: string;
}

const SAMPLE = /^([A-Za-z_:][\w:]*)(?:\{(.*)\})?\s+(\S+)(?:\s+-?\d+)?$/;

export function isPromSampleLine(line: string): boolean {
  return SAMPLE.test(line.trim());
}

export function parsePrometheusText(text: string, max = 2000): PromSample[] {
  const out: PromSample[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const m = SAMPLE.exec(line);
    if (!m) throw new Error(`Not a Prometheus sample line: ${line.slice(0, 60)}`);
    const labels: Record<string, string> = {};
    const re = /([A-Za-z_]\w*)="((?:[^"\\]|\\.)*)"/g;
    for (let l = re.exec(m[2] ?? ''); l; l = re.exec(m[2] ?? '')) labels[l[1]] = l[2].replace(/\\(.)/g, '$1');
    out.push({ name: m[1], labels, value: m[3] });
    if (out.length >= max) break;
  }
  return out;
}
